import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TablePagination,
  TextField,
} from "@mui/material";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  deleteAgencyBookingReportRule,
  getAgencyBookingReportRules,
} from "../redux/actions/AdminAction";
import styled from "@emotion/styled";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import SearchIcon from "@mui/icons-material/Search";
import { GET_AGENCY_BOOKING_REPORT_RULE_RESET } from "../Constants/Constants";

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#282828",
    color: "#ffffff",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "#e8e9eb",
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

function createData(id, created_at, rule_name, pccs, active) {
  return { id, created_at, rule_name, pccs, active };
}

export default function AgencyBookingReport(props) {
  const { checkUserPermission } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [row, setRow] = React.useState([]);
  const [allRows, setAllRows] = React.useState([]);
  const [searchType, setSearchType] = React.useState("name");
  const [appliedSearch, setAppliedSearch] = React.useState({
    type: "name",
    value: "",
  });
  const [statusSearchValue, setStatusSearchValue] = React.useState("All");
  const searchInputRef = React.useRef(null);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [deleteRuleId, setDeleteRuleId] = React.useState(null);
  const [snackOpen, setSnackOpen] = React.useState(false);
  const [dialogOpen, setdialogOpen] = React.useState(false);
  const statusFilterOptions = ["All", "Active", "Inactive"];

  const GetAgencyBookingReportRules = useSelector(
    (state) => state.GetAgencyBookingReportRules
  );
  const { agencyBookingReportRulesInfo } = GetAgencyBookingReportRules;

  const DeleteAgencyBookingReportRule = useSelector(
    (state) => state.DeleteAgencyBookingReportRule
  );
  const { success, error } = DeleteAgencyBookingReportRule;

  const handleDialogOpen = () => {
    setdialogOpen(true);
  };

  const handleDialogClose = () => {
    setdialogOpen(false);
  };

  const handleSnackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackOpen(false);
  };

  React.useEffect(() => {
    dispatch(getAgencyBookingReportRules());
  }, [dispatch]);

  React.useEffect(() => {
    if (agencyBookingReportRulesInfo) {
      const newRows = Object.values(agencyBookingReportRulesInfo).map(
        (value) => {
          return createData(
            value.agency_rules_id,
            value.created_at,
            value.rule_name,
            value.pccs,
            value.active
          );
        }
      );
      setAllRows(newRows);
    }
  }, [agencyBookingReportRulesInfo]);

  React.useEffect(() => {
    let filteredRows = [...allRows];

    const searchValue = appliedSearch.value.trim().toUpperCase();

    if (appliedSearch.type === "status" && searchValue && searchValue !== "ALL") {
      filteredRows = filteredRows.filter((value) =>
        searchValue === "ACTIVE" ? value.active : !value.active
      );
    }

    if (appliedSearch.type !== "status" && searchValue) {
      filteredRows = filteredRows.filter((value) => {
        if (appliedSearch.type === "pcc") {
          return (value.pccs || "").toUpperCase().includes(searchValue);
        }

        return (value.rule_name || "").toUpperCase().includes(searchValue);
      });
    }

    setRow(filteredRows);
    setPage(0);
  }, [allRows, appliedSearch]);

  const handleAgencyBookingSearch = () => {
    const value =
      searchType === "status"
        ? statusSearchValue
        : (searchInputRef.current?.value || "").trim().toUpperCase();

    setAppliedSearch({
      type: searchType,
      value,
    });
  };

  const displayedRows = React.useMemo(() => {
    const firstRowIndex = page * rowsPerPage;
    return row.slice(firstRowIndex, firstRowIndex + rowsPerPage);
  }, [page, row, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <React.Fragment>
      <Container>
        <Box>
          <Box sx={{ margin: 3, fontSize: 25 }}>
            Manage Agency Booking Report Rules
            <Divider sx={{ marginTop: 2 }}></Divider>
          </Box>
          <Box
            sx={{
              fontSize: 25,
              margin: 3,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            {checkUserPermission(106, "can_add") && (
              <Button
                type="submit"
                size="small"
                onClick={() => {
                  dispatch({ type: GET_AGENCY_BOOKING_REPORT_RULE_RESET });
                  navigate("/agencyBookingReportRules/new");
                }}
                sx={{
                  backgroundColor: "#282828",
                  color: "#ffffff",
                  width: "18vh",
                  borderRadius: 0,
                  fontSize: 16,
                  "&:hover": {
                    backgroundColor: "#282828",
                    color: "#ffffff",
                  },
                }}
              >
                Add new rule
              </Button>
            )}

            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                flexWrap: "wrap",
                justifyContent: "flex-end",
              }}
            >
              <FormControl size="small" sx={{ minWidth: "18vh" }}>
                <InputLabel id="agency-booking-search-by-label">
                  Search by
                </InputLabel>
                <Select
                  labelId="agency-booking-search-by-label"
                  id="agency-booking-search-by"
                  value={searchType}
                  label="Search by"
                  onChange={(e) => {
                    const nextSearchType = e.target.value;
                    setSearchType(nextSearchType);
                    setAppliedSearch((prevState) => ({
                      ...prevState,
                      type: nextSearchType,
                      value: "",
                    }));
                    setStatusSearchValue("All");
                    if (searchInputRef.current) {
                      searchInputRef.current.value = "";
                    }
                  }}
                >
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="pcc">PCC</MenuItem>
                  <MenuItem value="status">Status</MenuItem>
                </Select>
              </FormControl>
              {searchType === "status" ? (
                <FormControl size="small" sx={{ minWidth: "18vh" }}>
                  <InputLabel id="agency-booking-status-label">Status</InputLabel>
                  <Select
                    labelId="agency-booking-status-label"
                    id="agency-booking-status"
                    value={statusSearchValue}
                    label="Status"
                    onChange={(e) => {
                      setStatusSearchValue(e.target.value);
                    }}
                  >
                    {statusFilterOptions.map((statusOption) => (
                      <MenuItem key={statusOption} value={statusOption}>
                        {statusOption}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  inputRef={searchInputRef}
                  size="small"
                  sx={{ width: "28vh" }}
                  placeholder={`Search by ${
                    searchType === "pcc" ? "PCC" : "Name"
                  }`}
                  onChange={(e) => {
                    const formattedValue = e.target.value.toUpperCase();
                    if (formattedValue !== e.target.value) {
                      e.target.value = formattedValue;
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleAgencyBookingSearch();
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              <Button
                variant="contained"
                size="small"
                onClick={handleAgencyBookingSearch}
                sx={{
                  backgroundColor: "#282828",
                  color: "#ffffff",
                  minWidth: "12vh",
                  borderRadius: 0,
                  "&:hover": {
                    backgroundColor: "#282828",
                    color: "#ffffff",
                  },
                }}
              >
                Search
              </Button>
            </Box>
          </Box>
        </Box>
      </Container>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          marginBottom: "5%",
        }}
      >
        <TableContainer
          component={Paper}
          sx={{
            width: "80%",
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell>No.</StyledTableCell>
                <StyledTableCell>Creation Date</StyledTableCell>
                <StyledTableCell>Agencies</StyledTableCell>
                <StyledTableCell>PCCs</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell>Edit</StyledTableCell>
                <StyledTableCell>Delete</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedRows?.length > 0 ? (
                displayedRows.map((row, index) => (
                  <StyledTableRow key={row.id}>
                    <StyledTableCell component="th" scope="row">
                      {page * rowsPerPage + index + 1}
                    </StyledTableCell>
                    <StyledTableCell component="th" scope="row">
                      {row.created_at}
                    </StyledTableCell>
                    <StyledTableCell
                      component="th"
                      scope="row"
                      style={{
                        wordWrap: "break-word",
                        maxWidth: "100px",
                      }}
                    >
                      {row.rule_name}
                    </StyledTableCell>
                    <StyledTableCell
                      style={{
                        wordWrap: "break-word",
                        maxWidth: "500px",
                      }}
                    >
                      {row.pccs}
                    </StyledTableCell>
                    <StyledTableCell
                      style={{
                        wordWrap: "break-word",
                        maxWidth: "100px",
                      }}
                    >
                      {row.active ? "Active" : "Inactive"}
                    </StyledTableCell>
                    <StyledTableCell
                      style={{
                        wordWrap: "break-word",
                        maxWidth: "500px",
                        textAlign: "left",
                      }}
                    >
                      <Button
                        disabled={!checkUserPermission(106, "can_edit")}
                        sx={{
                          backgroundColor: !checkUserPermission(106, "can_edit")
                            ? "#D8D9DA"
                            : "#ffffff",
                          border: !checkUserPermission(106, "can_edit")
                            ? 0
                            : "1px solid black",
                          color: "black",
                          fontSize: 12,
                          height: "30px",
                        }}
                        onClick={() =>
                          navigate(`/agencyBookingReportRules/${row.id}`)
                        }
                      >
                        Edit
                      </Button>
                    </StyledTableCell>
                    <StyledTableCell
                      style={{
                        wordWrap: "break-word",
                        maxWidth: "500px",
                        textAlign: "left",
                      }}
                    >
                      <Button
                        disabled={!checkUserPermission(106, "can_delete")}
                        sx={{
                          backgroundColor: !checkUserPermission(
                            106,
                            "can_delete"
                          )
                            ? "#D8D9DA"
                            : "#d9534f",
                          color: "white",
                          fontSize: 12,
                          height: "30px",
                          "&:hover": {
                            backgroundColor: "#d9534f",
                            color: "white",
                          },
                        }}
                        onClick={() => {
                          setdialogOpen(true);
                          setDeleteRuleId(row.id);
                        }}
                      >
                        Delete
                      </Button>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              ) : (
                <StyledTableRow
                  style={{
                    wordWrap: "break-word",
                    maxWidth: "100%",
                  }}
                >
                    <StyledTableCell
                    component="th"
                    colSpan={7}
                    scope="row"
                    style={{
                      textAlign: "center",
                      wordWrap: "break-word",
                      maxWidth: "100%",
                      fontSize: 15,
                    }}
                  >
                    No data found
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={row.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </TableContainer>
        {success && (
          <Snackbar
            open={snackOpen}
            autoHideDuration={5000}
            onClose={handleSnackClose}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            sx={{ width: "20%" }}
          >
            <Alert
              onClose={handleSnackClose}
              severity="success"
              sx={{ width: "100%" }}
            >
              <AlertTitle>Success</AlertTitle>
              Record deleted successfully
            </Alert>
          </Snackbar>
        )}
        {error && (
          <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            open={snackOpen}
            autoHideDuration={5000}
            onClose={handleSnackClose}
            sx={{ width: "20%" }}
          >
            <Alert
              onClose={handleSnackClose}
              severity="error"
              sx={{ width: "100%" }}
            >
              <AlertTitle>Error</AlertTitle>
              {error}
            </Alert>
          </Snackbar>
        )}
        <Dialog
          open={dialogOpen}
          onClose={handleDialogOpen}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">Opps!</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to delete this rule?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>No</Button>
            <Button
              onClick={() => {
                dispatch(deleteAgencyBookingReportRule(deleteRuleId)).then(() => {
                  dispatch(getAgencyBookingReportRules());
                  setSnackOpen(true);
                  handleDialogClose();
                });
              }}
              autoFocus
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </React.Fragment>
  );
}
